import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useFormik } from "formik"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap"

import { createCustomer, getCustomers } from "api/customerApi"
import { getVehicleImporters } from "api/vehicleImporterApi"
import { downloadInStockVehicleSpareKeysPdf, getVehicleSpareKeyCustomerSuggestions } from "api/vehicleSpareKeyApi"
import DeleteModal from "components/Common/DeleteModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import TableContainer from "components/Common/TableContainer"
import ThemeModal from "components/Common/ThemeModal"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useListParams } from "hooks/useListParams"
import {
  useCreateVehicleSpareKey,
  useDeleteVehicleSpareKey,
  useEditVehicleSpareKey,
  useVehicleSpareKey,
  useVehicleSpareKeys,
} from "hooks/useVehicleSpareKeys"
import { formatReadableDate, toLocalDatetimeInput } from "utils/formatDate"
import CustomerForm from "pages/CustomerPages/Customer/CustomerForm"

const STATUS_OPTIONS = [
  { value: "in_stock", label: "In Stock", color: "success", icon: "bx-package" },
  { value: "given_to_customer", label: "Given to Customer", color: "primary", icon: "bx-user-check" },
]

const statusLabels = {
  in_stock: "In Stock",
  given_to_customer: "Given to Customer",
}

const statusColors = {
  in_stock: "success",
  given_to_customer: "primary",
}

const nowLocal = () => toLocalDatetimeInput(new Date())
const parseLocalDateTime = value => {
  if (!value) return null
  const normalized = String(value).includes("T")
    ? String(value)
    : String(value).replace(" ", "T")
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

const toDatetimeInput = value => {
  if (!value) return ""
  const date = parseLocalDateTime(value)
  if (!date) return String(value).replace(" ", "T").slice(0, 16)
  return toLocalDatetimeInput(date)
}

const formatSpareKeyDateTime = value => {
  const date = parseLocalDateTime(value)
  return date ? formatReadableDate(date) : "-"
}

const normalizeOptionList = response => {
  const data = response?.data?.data || response?.data || []
  return Array.isArray(data) ? data : []
}

const vehicleInitialValue = item => {
  if (!item?.vehicle_stock_id && !item?.vehicleStock && !item?.vehicle_stock) {
    return ""
  }

  const vehicle = item.vehicleStock || item.vehicle_stock
  if (!vehicle) {
    return item.vehicle_stock_id || ""
  }

  return {
    value: vehicle.id,
    label: [vehicle.stock_id, vehicle.full_chassis].filter(Boolean).join(" - "),
    raw: vehicle,
  }
}

const getVehicle = item => item?.vehicleStock || item?.vehicle_stock
const getImporter = item => item?.vehicleImporter || item?.vehicle_importer

const customerOption = customer => ({
  value: customer.id,
  label: [customer.name, customer.mobile, customer.cuid].filter(Boolean).join(" - "),
})

const uniqueSuggestedCustomers = suggestions => {
  const byId = new Map()
  suggestions.forEach(item => {
    if (item?.customer?.id && !byId.has(item.customer.id)) {
      byId.set(item.customer.id, item.customer)
    }
  })
  return Array.from(byId.values())
}

const CustomerOnboardModal = ({ isOpen, toggle, onCreated, initialName = "" }) => {
  const [saving, setSaving] = useState(false)
  const {
    raw: rawData,
  } = useFilteredVehicleOptions()

  const customerData = rawData?.customer ?? {}

  const formOptions = useMemo(
    () => ({
      designations: customerData?.designations ?? [],
      companies: customerData?.companies ?? [],
      banks: customerData?.banks ?? [],
      branches: customerData?.branches ?? [],
      customerTypes: customerData?.customer_types ?? [],
    }),
    [customerData]
  )

  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      setSaving(true)
      try {
        const res = await createCustomer(values)
        toast.success(res?.message ?? "Customer created successfully.")
        onCreated?.(res?.data)
        resetForm()
        toggle()
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to create customer.")
      } finally {
        setSaving(false)
      }
    },
    [onCreated, toggle]
  )

  return (
    <ThemeModal isOpen={isOpen} toggle={toggle} title="Onboard Customer">
      <CustomerForm
        initialData={{ customer_type: "general", name: initialName }}
        options={formOptions}
        onSubmit={handleSubmit}
        onCancel={toggle}
        isEdit={false}
        isLoading={saving}
        submitButtonText={saving ? "Saving..." : "Add Customer"}
        showAdditionalInfo={true}
      />
    </ThemeModal>
  )
}

const VehicleSpareKeyForm = ({ modal, toggleModal, isEdit, editedData, onSuccess, forceGiveMode = false }) => {
  const [suggestions, setSuggestions] = useState([])
  const [suggestionLoading, setSuggestionLoading] = useState(false)
  const [suggestionChecked, setSuggestionChecked] = useState(false)
  const [customerModal, setCustomerModal] = useState(false)
  const [onboardedCustomers, setOnboardedCustomers] = useState([])

  const { mutateAsync: createMutation, isPending: createLoading } = useCreateVehicleSpareKey()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditVehicleSpareKey()

  const { data: importerResponse, isLoading: importerLoading } = useQuery({
    queryKey: ["vehicle-importers", "vehicle-spare-key"],
    queryFn: () => getVehicleImporters({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const { data: customerResponse, isLoading: customerLoading, refetch: refetchCustomers } = useQuery({
    queryKey: ["customers", "vehicle-spare-key"],
    queryFn: () => getCustomers({ all: true, per_page: 300 }),
    enabled: modal,
  })

  const importerOptions = useMemo(
    () => normalizeOptionList(importerResponse).map(item => ({ value: item.id, label: item.name })),
    [importerResponse]
  )

  const customerOptions = useMemo(
    () => [
      ...normalizeOptionList(customerResponse).map(customerOption),
      ...onboardedCustomers.map(customerOption),
    ],
    [customerResponse, onboardedCustomers]
  )

  const validationSchema = Yup.object({
    vehicle_id: Yup.mixed().nullable(),
    full_chassis: Yup.string().trim().when("vehicle_id", {
      is: value => !value,
      then: schema => schema.required("Please select a vehicle or enter chassis/tracking text"),
      otherwise: schema => schema.nullable(),
    }),
    vehicle_importer_id: Yup.mixed().nullable(),
    status: Yup.string().oneOf(["in_stock", "given_to_customer"]).required("Please select status"),
    received_date: Yup.string().nullable(),
    customer_id: Yup.mixed().when("status", {
      is: "given_to_customer",
      then: schema => schema.required("Please select customer"),
      otherwise: schema => schema.nullable(),
    }),
    given_date: Yup.string().when("status", {
      is: "given_to_customer",
      then: schema => schema.required("Please select given date"),
      otherwise: schema => schema.nullable(),
    }),
    remarks: Yup.string().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      vehicle_id: vehicleInitialValue(editedData),
      full_chassis: editedData?.full_chassis || getVehicle(editedData)?.full_chassis || "",
      vehicle_importer_id: editedData?.vehicle_importer_id || getImporter(editedData)?.id || "",
      status: forceGiveMode ? "given_to_customer" : editedData?.status || "in_stock",
      received_date: toDatetimeInput(editedData?.received_date) || nowLocal(),
      customer_id: editedData?.customer_id || editedData?.customer?.id || "",
      given_date: forceGiveMode ? nowLocal() : toDatetimeInput(editedData?.given_date),
      remarks: editedData?.remarks || "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const vehicleValue = values.vehicle_id?.value ?? values.vehicle_id
      const vehicleStockId = vehicleValue && !Number.isNaN(Number(vehicleValue)) ? Number(vehicleValue) : null
      const typedTracking = values.full_chassis?.trim() || (!vehicleStockId && vehicleValue ? String(vehicleValue).trim() : "")

      const payload = {
        vehicle_stock_id: vehicleStockId,
        full_chassis: typedTracking || null,
        vehicle_importer_id: values.vehicle_importer_id || null,
        status: values.status,
        received_date: values.received_date || null,
        customer_id: values.status === "given_to_customer" ? values.customer_id || null : null,
        given_date: values.status === "given_to_customer" ? values.given_date || null : null,
        remarks: values.remarks?.trim() || null,
      }

      try {
        const res = isEdit
          ? await editMutation({ id: editedData.id, ...payload })
          : await createMutation(payload)
        toast.success(res?.message ?? `Spare key ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        onSuccess?.(res?.data)
        toggleModal()
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to save spare key.")
      }
    },
  })

  const loadingAny = createLoading || editLoading || importerLoading || customerLoading

  const handleVehicleSelect = vehicle => {
    if (vehicle?.full_chassis) {
      formik.setFieldValue("full_chassis", vehicle.full_chassis)
      setSuggestions([])
      setSuggestionChecked(false)
    }
  }

  const handleCheckCustomers = useCallback(async () => {
    const fullChassis = formik.values.full_chassis?.trim()

    if (!fullChassis || fullChassis.length < 4) {
      toast.error("Enter at least 4 characters of full chassis before checking customers.")
      return
    }

    setSuggestionLoading(true)
    setSuggestionChecked(false)

    try {
      const res = await getVehicleSpareKeyCustomerSuggestions({ full_chassis: fullChassis })
      const rows = Array.isArray(res?.data) ? res.data : []
      setSuggestions(rows)
      setSuggestionChecked(true)

      const customers = uniqueSuggestedCustomers(rows)
      if (customers.length === 1) {
        formik.setFieldValue("customer_id", customers[0].id)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to check customers.")
    } finally {
      setSuggestionLoading(false)
    }
  }, [formik])

  const handleSuggestionSelect = customer => {
    if (!customer?.id) return
    formik.setFieldValue("customer_id", customer.id)
  }

  const handleCustomerCreated = customer => {
    if (!customer?.id) return
    setOnboardedCustomers(prev => {
      if (prev.some(item => Number(item.id) === Number(customer.id))) {
        return prev
      }
      return [...prev, customer]
    })
    formik.setFieldValue("customer_id", customer.id)
    setSuggestionChecked(false)
    setSuggestions([])
    refetchCustomers()
  }

  return (
    <>
      <ThemeModal
        isOpen={modal}
        toggle={toggleModal}
        title={forceGiveMode ? "Give Spare Key to Customer" : isEdit ? "Edit Spare Key" : "Receive Spare Key"}
        size="lg"
      >
        <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        {!forceGiveMode && (
          <Card className="border">
            <CardBody>
              <h5 className="mb-3">Spare Key Tracking</h5>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <VehicleIdSelect formik={formik} onVehicleSelect={handleVehicleSelect} />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="full_chassis">
                      Full Chassis / Tracking Text <span className="text-danger">*</span>
                    </Label>
                    <InputField
                      type="text"
                      name="full_chassis"
                      id="full_chassis"
                      value={formik.values.full_chassis}
                      onChange={formik.handleChange}
                      invalid={formik.touched.full_chassis && !!formik.errors.full_chassis}
                    />
                    {formik.touched.full_chassis && formik.errors.full_chassis && (
                      <FormFeedback type="invalid">{formik.errors.full_chassis}</FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormikCreatableSelect
                    name="vehicle_importer_id"
                    label="Received From Importer"
                    placeholder="Select importer"
                    formik={formik}
                    options={importerOptions}
                    loading={importerLoading}
                  />
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="received_date">Received Date Time</Label>
                    <InputField
                      type="datetime-local"
                      name="received_date"
                      id="received_date"
                      value={formik.values.received_date}
                      onChange={formik.handleChange}
                      invalid={formik.touched.received_date && !!formik.errors.received_date}
                    />
                    {formik.touched.received_date && formik.errors.received_date && (
                      <FormFeedback type="invalid">{formik.errors.received_date}</FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
              <StatusButtonGroup
                name="status"
                label="Status"
                value={formik.values.status}
                onChange={value => {
                  formik.setFieldValue("status", value)
                  if (value === "given_to_customer" && !formik.values.given_date) {
                    formik.setFieldValue("given_date", nowLocal())
                  }
                }}
                options={STATUS_OPTIONS}
                disabled={forceGiveMode}
                required
              />
            </CardBody>
          </Card>
        )}

        {formik.values.status === "given_to_customer" && (
          <Card className="border mt-3">
            <CardBody>
              <h5 className="mb-3">Customer Handover</h5>
              <Row className="align-items-end">
                <Col md={8}>
                  <div className="mb-3">
                    <Label className="mb-1">Lookup by Full Chassis</Label>
                    <Input
                      bsSize="sm"
                      value={formik.values.full_chassis}
                      onChange={e => {
                        formik.setFieldValue("full_chassis", e.target.value)
                        setSuggestions([])
                        setSuggestionChecked(false)
                      }}
                      placeholder="Enter full chassis to find money receipt customers"
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Button
                      color="primary"
                      outline
                      type="button"
                      className="w-100"
                      onClick={handleCheckCustomers}
                      disabled={suggestionLoading}
                    >
                      <i className={`bx bx-search me-1 ${suggestionLoading ? "bx-spin" : ""}`} />
                      {suggestionLoading ? "Checking..." : "Check Customers"}
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormikCreatableSelect
                    name="customer_id"
                    label="Customer"
                    formik={formik}
                    options={customerOptions}
                    loading={customerLoading}
                    required
                  />
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="given_date">
                      Given Date Time <span className="text-danger">*</span>
                    </Label>
                    <InputField
                      type="datetime-local"
                      name="given_date"
                      id="given_date"
                      value={formik.values.given_date}
                      onChange={formik.handleChange}
                      invalid={formik.touched.given_date && !!formik.errors.given_date}
                    />
                    {formik.touched.given_date && formik.errors.given_date && (
                      <FormFeedback type="invalid">{formik.errors.given_date}</FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
              {suggestionChecked && suggestions.length > 0 && (
                <div className="border rounded p-2 mb-3">
                  <div className="small fw-semibold mb-2">Money receipt matches</div>
                  <div className="d-grid gap-2">
                    {suggestions.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        className="btn btn-light text-start border"
                        onClick={() => handleSuggestionSelect(item.customer)}
                      >
                        <div className="d-flex flex-wrap justify-content-between gap-2">
                          <strong>{item.customer?.name || "Unknown customer"}</strong>
                          <span className="text-muted">{item.mr_no || `MR #${item.id}`}</span>
                        </div>
                        <div className="small text-muted">
                          {[item.customer?.mobile, item.issued_date ? formatSpareKeyDateTime(item.issued_date) : null, item.vehicle?.stock_id, item.vehicle?.full_chassis]
                            .filter(Boolean)
                            .join(" | ")}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestionChecked && suggestions.length === 0 && !formik.values.customer_id && (
                <div className="border rounded p-3 mb-3">
                  <div className="text-muted small mb-2">
                    No customer found from money receipts for this chassis.
                  </div>
                  <Button
                    color="success"
                    type="button"
                    size="sm"
                    onClick={() => setCustomerModal(true)}
                  >
                    <i className="bx bx-user-plus me-1" />
                    Onboard Customer
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        <Card className="border mt-3">
          <CardBody>
            <Label htmlFor="remarks">Remarks</Label>
            <InputField
              type="textarea"
              name="remarks"
              id="remarks"
              value={formik.values.remarks}
              onChange={formik.handleChange}
            />
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : forceGiveMode ? "Give to Customer" : isEdit ? "Save Changes" : "Receive Spare Key"}
          </Button>
        </div>
        </Form>
      </ThemeModal>
      <CustomerOnboardModal
        isOpen={customerModal}
        toggle={() => setCustomerModal(prev => !prev)}
        onCreated={handleCustomerCreated}
      />
    </>
  )
}

const DetailLine = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
    <span className="text-muted">{label}</span>
    <span className="fw-semibold text-end">{value || "-"}</span>
  </div>
)

const HistoryStep = ({ color, icon, title, subtitle, children, muted = false }) => (
  <div className="d-flex gap-3">
    <div
      className={`rounded-circle bg-${color} text-white d-flex align-items-center justify-content-center flex-shrink-0`}
      style={{ width: 38, height: 38 }}
    >
      <i className={`bx ${icon}`} />
    </div>
    <div className={`flex-grow-1 pb-3 ${muted ? "text-muted" : ""}`}>
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <h6 className="mb-1">{title}</h6>
        {subtitle && <span className="small text-muted">{subtitle}</span>}
      </div>
      {children}
    </div>
  </div>
)

const DateTimelineItem = ({ id, color, label, value, isLast = false }) => (
  <div className="d-flex gap-2 small position-relative">
    <div className="d-flex flex-column align-items-center flex-shrink-0">
      <span
        id={id}
        className={`rounded-circle bg-${color}`}
        style={{ width: 9, height: 9, marginTop: 5 }}
      />
      {!isLast && <span className="border-start flex-grow-1 my-1" />}
    </div>
    <div className="pb-1">
      <span className="text-muted">{label}</span>
      <span className="ms-1">{formatSpareKeyDateTime(value)}</span>
      <UncontrolledTooltip placement="top" target={id}>
        {label}: {formatSpareKeyDateTime(value)}
      </UncontrolledTooltip>
    </div>
  </div>
)

const DateStack = ({ item }) => {
  const timeline = [
    { key: "received", color: "success", label: "Received", value: item.received_date },
    { key: "given", color: "primary", label: "Given", value: item.given_date },
    { key: "created", color: "secondary", label: "Created", value: item.created_at },
    { key: "updated", color: "info", label: "Updated", value: item.updated_at },
  ]

  return (
    <div style={{ width: 260 }}>
      {timeline.map((entry, index) => (
        <DateTimelineItem
          key={entry.key}
          id={`${entry.key}-date-${item.id}`}
          color={entry.color}
          label={entry.label}
          value={entry.value}
          isLast={index === timeline.length - 1}
        />
      ))}
    </div>
  )
}

const CustomerProfileLink = ({ customer }) => {
  if (!customer?.id) return "-"

  const identifier = customer.cuid || customer.id

  return (
    <Link
      to={`/customer-list/${identifier}/view`}
      className="d-inline-flex align-items-center text-primary text-decoration-none"
    >
      <i className="mdi mdi-account-circle-outline me-1" />
      <span>{customer.name || `Customer #${customer.id}`}</span>
    </Link>
  )
}

const nameOf = value => value?.name || value || ""

const VehicleStockSummary = ({ item }) => {
  const vehicle = getVehicle(item)
  const fullChassis = item?.full_chassis || vehicle?.full_chassis

  if (!vehicle && !fullChassis) return "-"

  const title = [
    nameOf(vehicle?.brand),
    nameOf(vehicle?.model),
    nameOf(vehicle?.year),
  ].filter(Boolean).join(" ")

  const meta = [
    nameOf(vehicle?.exterior_color),
    vehicle?.mileage ? `${vehicle.mileage} km` : "",
    nameOf(vehicle?.engine_type),
  ].filter(Boolean)

  return (
    <div style={{ minWidth: 180 }}>
      <div className="fw-semibold">{vehicle?.stock_id || "-"}</div>
      {fullChassis && <div className="small">{fullChassis}</div>}
      {title && <div className="small text-muted">{title}</div>}
      {meta.length > 0 && <div className="small text-muted">{meta.join(" | ")}</div>}
    </div>
  )
}

const VehicleSpareKeyView = ({ modal, toggleModal, spareKey, loading }) => {
  const vehicle = getVehicle(spareKey)
  const importer = getImporter(spareKey)
  const isGiven = spareKey?.status === "given_to_customer"
  const chassis = spareKey?.full_chassis || vehicle?.full_chassis || "-"

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Spare Key Details" size="lg">
      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <div className="text-muted small">Serial No</div>
              <h4 className="mb-1">{spareKey?.serial_no || "-"}</h4>
              <div className="text-muted">{chassis}</div>
            </div>
            <Badge color={statusColors[spareKey?.status] || "secondary"} className="font-size-12 px-3 py-2">
              {statusLabels[spareKey?.status] || spareKey?.status || "-"}
            </Badge>
          </div>

          <Row className="g-3">
            <Col md={5}>
              <Card className="border h-100 mb-0">
                <CardBody>
                  <h6 className="mb-3">Vehicle Identity</h6>
                  <DetailLine label="Stock ID" value={vehicle?.stock_id} />
                  <DetailLine label="Full Chassis" value={chassis} />
                  <DetailLine label="Importer" value={importer?.name} />
                  <DetailLine label="Customer" value={spareKey?.customer?.name} />
                  <div className="pt-3">
                    <div className="text-muted small mb-1">Remarks</div>
                    <div className="fw-semibold">{spareKey?.remarks || "-"}</div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={7}>
              <Card className="border h-100 mb-0">
                <CardBody>
                  <h6 className="mb-3">Spare Key History</h6>

                  <HistoryStep
                    color="success"
                    icon="bx-package"
                    title="Received from Importer"
                    subtitle={formatSpareKeyDateTime(spareKey?.received_date)}
                  >
                    <div className="small">
                      <div><span className="text-muted">Received From:</span> <span className="fw-semibold">{importer?.name || "Not specified"}</span></div>
                      <div><span className="text-muted">Tracking:</span> <span className="fw-semibold">{chassis}</span></div>
                    </div>
                  </HistoryStep>

                  <div className="ms-3 border-start border-2" style={{ minHeight: 14 }} />

                  <HistoryStep
                    color={isGiven ? "primary" : "secondary"}
                    icon={isGiven ? "bx-user-check" : "bx-time-five"}
                    title={isGiven ? "Given to Customer" : "Awaiting Customer Handover"}
                    subtitle={isGiven ? formatSpareKeyDateTime(spareKey?.given_date) : null}
                    muted={!isGiven}
                  >
                    {isGiven ? (
                      <div className="small">
                        <div><span className="text-muted">Customer:</span> <span className="fw-semibold">{spareKey?.customer?.name || "-"}</span></div>
                        <div><span className="text-muted">Mobile:</span> <span className="fw-semibold">{spareKey?.customer?.mobile || "-"}</span></div>
                      </div>
                    ) : (
                      <div className="small">Spare key is currently available in inventory.</div>
                    )}
                  </HistoryStep>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </ThemeModal>
  )
}

const VehicleSpareKeyList = () => {
  document.title = "Vehicle Spare Key List"

  const [modal, setModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [giveMode, setGiveMode] = useState(false)
  const [isPdfDownloading, setIsPdfDownloading] = useState(false)

  const { params, updateParams, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "", status: "in_stock" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search, status } = params
    return { page, per_page, search, ...(status && status !== "all" && { status }) }
  }, [params])

  const { data: responseData, isLoading, refetch, isRefetching } = useVehicleSpareKeys(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useVehicleSpareKey(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteVehicleSpareKey()

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setGiveMode(false)
    }
  }, [modal])

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem({ id: selectedData.id })
      toast.success(res?.message ?? "Spare key deleted successfully.")
      setDeleteModal(false)
      setSelectedData(null)
      refetch()
    } catch {
      toast.error("Failed to delete spare key.")
    }
  }

  const resetAll = () => {
    resetParams()
  }

  const setStatusFilter = value => {
    updateParams(prev => ({ ...prev, status: value, page: 1 }))
  }

  const handleInStockPdf = async () => {
    try {
      await downloadInStockVehicleSpareKeysPdf({
        params: { ...(params.search && { search: params.search }) },
        onStart: () => setIsPdfDownloading(true),
        onEnd: () => setIsPdfDownloading(false),
      })
    } catch {
      toast.error("Failed to download in-stock spare key PDF.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "SL",
        cell: ({ row }) => ((paginationInfo?.current_page || 1) - 1) * (paginationInfo?.per_page || 10) + row.index + 1,
      },
      { header: "Serial No", accessorKey: "serial_no" },
      {
        header: "Vehicle Details",
        cell: ({ row }) => <VehicleStockSummary item={row.original} />,
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <Badge color={statusColors[row.original.status] || "secondary"}>
            {statusLabels[row.original.status] || row.original.status}
          </Badge>
        ),
      },
      {
        header: "Customer",
        cell: ({ row }) => <CustomerProfileLink customer={row.original.customer} />,
      },
      {
        header: "Dates",
        cell: ({ row }) => <DateStack item={row.original} />,
        meta: { style: { width: 280, minWidth: 280 } },
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button color="primary" size="sm" onClick={() => { setSelectedViewId(row.original.id); setViewModal(true) }} id={`view-spare-key-${row.original.id}`}>
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`view-spare-key-${row.original.id}`}>View</UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.VEHICLE_SPARE_KEY.EDIT]}>
              <li>
                <Button color="info" size="sm" onClick={() => { setSelectedData(row.original); setGiveMode(false); setModal(true) }} id={`edit-spare-key-${row.original.id}`}>
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-spare-key-${row.original.id}`}>Edit</UncontrolledTooltip>
              </li>
              {row.original.status === "in_stock" && (
                <li>
                  <Button color="success" size="sm" onClick={() => { setSelectedData(row.original); setGiveMode(true); setModal(true) }} id={`give-spare-key-${row.original.id}`}>
                    <i className="mdi mdi-account-arrow-right-outline" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`give-spare-key-${row.original.id}`}>Give to Customer</UncontrolledTooltip>
                </li>
              )}
            </CanView>
            <CanView access={[ACCESS.VEHICLE_SPARE_KEY.DELETE]}>
              <li>
                <Button color="danger" size="sm" onClick={() => { setSelectedData(row.original); setDeleteModal(true) }} id={`delete-spare-key-${row.original.id}`}>
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`delete-spare-key-${row.original.id}`}>Delete</UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    [paginationInfo]
  )

  return (
    <PageWrapper title="Vehicle Spare Key" breadcrumbItem="Spare Key List">
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <div>
          <h5 className="mb-1">Spare Key Inventory</h5>
          <div className="text-muted small">{paginationInfo?.total ?? 0} spare key records</div>
        </div>
        <div className="d-flex gap-2">
          <Button color="dark" onClick={handleInStockPdf} disabled={isPdfDownloading}>
            <i className={`mdi ${isPdfDownloading ? "mdi-progress-download" : "mdi-file-pdf-box"} me-1`} />
            In Stock PDF
          </Button>
          <Button color="light" onClick={refetch} disabled={isRefetching}>
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && (
            <Button color="secondary" outline onClick={resetAll}>Reset</Button>
          )}
          <CanView access={[ACCESS.VEHICLE_SPARE_KEY.ADD]}>
            <Button color="success" onClick={() => { setSelectedData(null); setGiveMode(false); setModal(true) }}>
              <i className="bx bx-plus me-1" />
              Receive Spare Key
            </Button>
          </CanView>
        </div>
      </div>

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
        <Col md={3}>
          <Label className="mb-1 small fw-semibold">Status</Label>
          <Input type="select" bsSize="sm" value={params.status || "in_stock"} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="in_stock">In Stock</option>
            <option value="given_to_customer">Given to Customer</option>
          </Input>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <VehicleSpareKeyView
        modal={viewModal}
        toggleModal={toggleViewModal}
        spareKey={viewResponse?.data}
        loading={isViewLoading}
      />

      <VehicleSpareKeyForm
        modal={modal}
        toggleModal={toggleModal}
        isEdit={!!selectedData}
        editedData={selectedData}
        forceGiveMode={giveMode}
        onSuccess={refetch}
      />
    </PageWrapper>
  )
}

export default VehicleSpareKeyList
